/* eslint-disable max-len */
import type { PricingIndexProfile } from './pricing-index-profile';
import { buildUserStories } from '../user-stories-ledger';

/**
 * DEMO_PROFILE — brand-neutral generic profile used at conferences and in
 * prospect previews. Structural parity with the customer profile (same counts of
 * indices, parts, comparable groups, clause register entries; same
 * baseline/review date window) so the engine produces a meaningful
 * forecast under any contract scenario, but with zero customer-specific
 * identifiers. Domain is "precision-machining contract manufacturer" —
 * aerospace, medical, semicon, industrial — but no real company is named.
 *
 * Brand-neutrality is enforced by demo-profile.spec.ts.
 */
export const DEMO_PROFILE: PricingIndexProfile = {
  profileId: 'demo',
  displayName: 'Your Organization',

  settings: {
    bidName: 'Sample Bid 2026-Q1',
    baselineDate: '2024-01-01',
    reviewDate: '2026-01-01',
    forwardMonths: 12,
    confidenceMultiplier: 1.0,
    minPurchasePoints: 2,
    minMonths: 6,
    highConfPoints: 5,
    highConfMonths: 18,
    weightMovement: 0.4,
    weightData: 0.2,
    weightCoverage: 0.2,
    weightRecovery: 0.2,
  },

  // Six indices — precision-machining roster (3 material PPI, 1 labour ECI,
  // 2 AHE labour series re-tagged as Material per the engine schema).
  // All six IDs map to real BLS series in REAL_BLS_DATA (bls-real-data.ts).
  indices: [
    { id: 'WPU10',           name: 'WPU10 Metals & Metal Products',         type: 'Material', materialWeight: 0.30, labourWeight: 0,   notes: 'BLS PPI' },
    { id: 'WPUID61113',      name: 'WPUID61113 Materials Durable Mfg',      type: 'Material', materialWeight: 0.20, labourWeight: 0,   notes: 'BLS PPI' },
    { id: 'ECI',             name: 'ECI Wages Private Industry',            type: 'Labour',   materialWeight: 0,    labourWeight: 1.0, notes: 'BLS ECI' },
    { id: 'PCU332710332710', name: 'Machine Shops PPI (NAICS 332710)',      type: 'Material', materialWeight: 0.20, labourWeight: 0,   notes: 'BLS PPI' },
    { id: 'CEU3133200008',   name: 'AHE Fabricated Metal Mfg (NAICS 332)',  type: 'Material', materialWeight: 0.15, labourWeight: 0,   notes: 'BLS CES' },
    { id: 'CEU3133320008',   name: 'AHE Industrial Machinery (NAICS 3332)', type: 'Material', materialWeight: 0.15, labourWeight: 0,   notes: 'BLS CES' },
  ],

  // Five comparable groups — precision machining segments.
  comparableGroups: [
    { id: 'PG.Titanium.Machined',     name: 'Titanium machined components',  description: 'Ti-6Al-4V machined parts' },
    { id: 'PG.Inconel.Rotating',      name: 'Inconel rotating/structural',   description: 'Inconel 718 rotating and structural parts' },
    { id: 'PG.Aluminum.Brackets',     name: 'Aluminum bracketry',            description: '7075 / 6061 aluminum brackets and housings' },
    { id: 'PG.Stainless.Implants',    name: 'Surgical-grade stainless',      description: '316LVM / 17-4PH stainless implant components' },
    { id: 'PG.Outside.Processing',    name: 'Outside processing',            description: 'Anodize / plating / heat-treat operations' },
  ],

  // Eight parts (matches the customer-profile count). SKUs carry no company prefix.
  // Suppliers are neutral names: Supplier A/B/C/D.
  parts: [
    { id: 'P001', name: 'Inconel 718 shaft',          sku: 'AS-INC718-SHAFT-0078',    supplier: 'Supplier A', comparableGroupId: 'PG.Inconel.Rotating',   primaryIndexId: 'WPU10',           secondaryIndexId: null,              passThrough: 'Yes',     recoveryMechanism: 'Standard escalation clause', inBidScope: true,  notes: 'Aerospace, Inconel 718' },
    { id: 'P002', name: '7075 aluminum bracket',      sku: 'AS-7075-BRKT-0142',       supplier: 'Supplier A', comparableGroupId: 'PG.Aluminum.Brackets',  primaryIndexId: 'WPU10',           secondaryIndexId: 'PCU332710332710', passThrough: 'Yes',     recoveryMechanism: 'Standard escalation clause', inBidScope: true,  notes: 'Aerospace bracket — 5-axis milled' },
    { id: 'P003', name: 'Ti-6Al-4V femoral stem',     sku: 'MD-TI64-FEMSTEM-0031',    supplier: 'Supplier B', comparableGroupId: 'PG.Titanium.Machined',  primaryIndexId: 'WPUID61113',      secondaryIndexId: 'PCU332710332710', passThrough: 'Partial', recoveryMechanism: 'Partial pass-through clause', inBidScope: true,  notes: 'Medical implant — titanium' },
    { id: 'P004', name: '316LVM surgical cannula',    sku: 'MD-316LVM-CANN-0204',     supplier: 'Supplier B', comparableGroupId: 'PG.Stainless.Implants', primaryIndexId: 'PCU332710332710', secondaryIndexId: null,              passThrough: 'Partial', recoveryMechanism: 'Partial pass-through clause', inBidScope: true,  notes: 'Swiss-turning, labour-heavy' },
    { id: 'P005', name: '6061 vacuum chamber lid',    sku: 'SC-AL6061-CHMR-0512',     supplier: 'Supplier C', comparableGroupId: 'PG.Aluminum.Brackets',  primaryIndexId: 'WPU10',           secondaryIndexId: 'CEU3133320008',   passThrough: 'No',      recoveryMechanism: 'Fixed price',                inBidScope: true,  notes: 'Semicon — single-source risk' },
    { id: 'P006', name: '17-4PH valve body',          sku: 'IN-17-4PH-VALVE-0331',    supplier: 'Supplier D', comparableGroupId: 'PG.Stainless.Implants', primaryIndexId: 'CEU3133200008',   secondaryIndexId: null,              passThrough: 'Yes',     recoveryMechanism: 'Standard escalation clause', inBidScope: true,  notes: 'EDM-heavy fabricated metal' },
    { id: 'P007', name: 'Actuator sub-assembly',      sku: 'EM-ASSY-ACTUATOR-0017',   supplier: 'Supplier D', comparableGroupId: 'PG.Outside.Processing', primaryIndexId: 'CEU3133320008',   secondaryIndexId: null,              passThrough: 'Yes',     recoveryMechanism: 'Standard escalation clause', inBidScope: false, notes: 'Out of bid scope — machinery assembly' },
    { id: 'P008', name: 'Inconel housing weldment',   sku: 'AS-INC718-HSG-0211',      supplier: 'Supplier A', comparableGroupId: 'PG.Inconel.Rotating',   primaryIndexId: 'CEU3133200008',   secondaryIndexId: 'WPU10',           passThrough: 'Yes',     recoveryMechanism: 'Standard escalation clause', inBidScope: true,  notes: 'Aerospace housing weldment' },
  ],

  // 25 purchase events distributed across the parts (matches the customer-profile count).
  purchases: [
    { partId: 'P001', date: '2024-01-15', unitPrice: 540,  quantity: 25,  supplier: 'Supplier A' },
    { partId: 'P001', date: '2024-08-01', unitPrice: 558,  quantity: 30,  supplier: 'Supplier A' },
    { partId: 'P001', date: '2025-04-01', unitPrice: 585,  quantity: 28,  supplier: 'Supplier A' },
    { partId: 'P001', date: '2025-12-01', unitPrice: 612,  quantity: 20,  supplier: 'Supplier A' },
    { partId: 'P002', date: '2024-02-01', unitPrice: 92,   quantity: 150, supplier: 'Supplier A' },
    { partId: 'P002', date: '2025-01-01', unitPrice: 98,   quantity: 175, supplier: 'Supplier A' },
    { partId: 'P002', date: '2025-11-01', unitPrice: 104,  quantity: 140, supplier: 'Supplier A' },
    { partId: 'P003', date: '2025-06-01', unitPrice: 2150, quantity: 12,  supplier: 'Supplier B' },
    { partId: 'P004', date: '2024-01-10', unitPrice: 380,  quantity: 60,  supplier: 'Supplier B' },
    { partId: 'P004', date: '2024-06-01', unitPrice: 388,  quantity: 65,  supplier: 'Supplier B' },
    { partId: 'P004', date: '2024-12-01', unitPrice: 402,  quantity: 70,  supplier: 'Supplier B' },
    { partId: 'P004', date: '2025-06-01', unitPrice: 418,  quantity: 60,  supplier: 'Supplier B' },
    { partId: 'P004', date: '2025-12-01', unitPrice: 435,  quantity: 55,  supplier: 'Supplier B' },
    { partId: 'P005', date: '2024-01-01', unitPrice: 1250, quantity: 18,  supplier: 'Supplier C' },
    { partId: 'P005', date: '2024-04-01', unitPrice: 1280, quantity: 20,  supplier: 'Supplier C' },
    { partId: 'P005', date: '2024-08-01', unitPrice: 1330, quantity: 18,  supplier: 'Supplier C' },
    { partId: 'P005', date: '2025-01-01', unitPrice: 1395, quantity: 22,  supplier: 'Supplier C' },
    { partId: 'P005', date: '2025-06-01', unitPrice: 1455, quantity: 20,  supplier: 'Supplier C' },
    { partId: 'P005', date: '2025-12-01', unitPrice: 1520, quantity: 18,  supplier: 'Supplier C' },
    { partId: 'P006', date: '2024-03-01', unitPrice: 215, quantity: 80,   supplier: 'Supplier D' },
    { partId: 'P006', date: '2024-11-01', unitPrice: 224, quantity: 85,   supplier: 'Supplier D' },
    { partId: 'P006', date: '2025-10-01', unitPrice: 236, quantity: 75,   supplier: 'Supplier D' },
    { partId: 'P007', date: '2024-05-01', unitPrice: 880, quantity: 14,   supplier: 'Supplier D' },
    { partId: 'P007', date: '2025-08-01', unitPrice: 928, quantity: 12,   supplier: 'Supplier D' },
    { partId: 'P008', date: '2025-09-01', unitPrice: 1180, quantity: 15,  supplier: 'Supplier A' },
  ],

  // Index history — same date window as the customer-profile seed (2023-01 → 2026-01).
  // Values follow the same general shape so the engine produces a comparable
  // forecast surface.
  indexHistory: [
    { indexId: 'WPU10',      date: '2023-01-01', value: 220,   source: 'BLS (illustrative seed)' },
    { indexId: 'WPU10',      date: '2023-07-01', value: 228,   source: 'BLS (illustrative seed)' },
    { indexId: 'WPU10',      date: '2024-01-01', value: 235,   source: 'BLS (illustrative seed)' },
    { indexId: 'WPU10',      date: '2024-07-01', value: 244,   source: 'BLS (illustrative seed)' },
    { indexId: 'WPU10',      date: '2025-01-01', value: 256,   source: 'BLS (illustrative seed)' },
    { indexId: 'WPU10',      date: '2025-07-01', value: 268,   source: 'BLS (illustrative seed)' },
    { indexId: 'WPU10',      date: '2026-01-01', value: 281,   source: 'BLS (illustrative seed)' },
    { indexId: 'WPUID61113', date: '2023-01-01', value: 195,   source: 'BLS (illustrative seed)' },
    { indexId: 'WPUID61113', date: '2023-07-01', value: 200,   source: 'BLS (illustrative seed)' },
    { indexId: 'WPUID61113', date: '2024-01-01', value: 207,   source: 'BLS (illustrative seed)' },
    { indexId: 'WPUID61113', date: '2024-07-01', value: 215,   source: 'BLS (illustrative seed)' },
    { indexId: 'WPUID61113', date: '2025-01-01', value: 223,   source: 'BLS (illustrative seed)' },
    { indexId: 'WPUID61113', date: '2025-07-01', value: 231,   source: 'BLS (illustrative seed)' },
    { indexId: 'WPUID61113', date: '2026-01-01', value: 240,   source: 'BLS (illustrative seed)' },
    { indexId: 'ECI',        date: '2023-01-01', value: 152,   source: 'BLS (illustrative seed)' },
    { indexId: 'ECI',        date: '2023-07-01', value: 154.5, source: 'BLS (illustrative seed)' },
    { indexId: 'ECI',        date: '2024-01-01', value: 157,   source: 'BLS (illustrative seed)' },
    { indexId: 'ECI',        date: '2024-07-01', value: 159.5, source: 'BLS (illustrative seed)' },
    { indexId: 'ECI',        date: '2025-01-01', value: 162,   source: 'BLS (illustrative seed)' },
    { indexId: 'ECI',        date: '2025-07-01', value: 164.5, source: 'BLS (illustrative seed)' },
    { indexId: 'ECI',        date: '2026-01-01', value: 167,   source: 'BLS (illustrative seed)' },
    { indexId: 'PCU332710332710', date: '2023-01-01', value: 197.858, source: 'BLS public API v2 (data.bls.gov, 2026-05-29 pull)' },
    { indexId: 'PCU332710332710', date: '2023-07-01', value: 200.887, source: 'BLS public API v2 (data.bls.gov, 2026-05-29 pull)' },
    { indexId: 'PCU332710332710', date: '2024-01-01', value: 200.455, source: 'BLS public API v2 (data.bls.gov, 2026-05-29 pull)' },
    { indexId: 'PCU332710332710', date: '2024-07-01', value: 202.351, source: 'BLS public API v2 (data.bls.gov, 2026-05-29 pull)' },
    { indexId: 'PCU332710332710', date: '2025-01-01', value: 207.453, source: 'BLS public API v2 (data.bls.gov, 2026-05-29 pull)' },
    { indexId: 'PCU332710332710', date: '2025-07-01', value: 209.059, source: 'BLS public API v2 (data.bls.gov, 2026-05-29 pull)' },
    { indexId: 'PCU332710332710', date: '2026-01-01', value: 211.017, source: 'BLS public API v2 (data.bls.gov, 2026-05-29 pull)' },
    { indexId: 'CEU3133200008',   date: '2023-01-01', value: 24.96,   source: 'BLS public API v2 (data.bls.gov, 2026-05-29 pull)' },
    { indexId: 'CEU3133200008',   date: '2023-07-01', value: 25.14,   source: 'BLS public API v2 (data.bls.gov, 2026-05-29 pull)' },
    { indexId: 'CEU3133200008',   date: '2024-01-01', value: 25.68,   source: 'BLS public API v2 (data.bls.gov, 2026-05-29 pull)' },
    { indexId: 'CEU3133200008',   date: '2024-07-01', value: 26.43,   source: 'BLS public API v2 (data.bls.gov, 2026-05-29 pull)' },
    { indexId: 'CEU3133200008',   date: '2025-01-01', value: 26.78,   source: 'BLS public API v2 (data.bls.gov, 2026-05-29 pull)' },
    { indexId: 'CEU3133200008',   date: '2025-07-01', value: 27.34,   source: 'BLS public API v2 (data.bls.gov, 2026-05-29 pull)' },
    { indexId: 'CEU3133200008',   date: '2026-01-01', value: 27.88,   source: 'BLS public API v2 (data.bls.gov, 2026-05-29 pull)' },
    { indexId: 'CEU3133320008',   date: '2023-01-01', value: 29.06,   source: 'BLS public API v2 (data.bls.gov, 2026-05-29 pull)' },
    { indexId: 'CEU3133320008',   date: '2023-07-01', value: 29.49,   source: 'BLS public API v2 (data.bls.gov, 2026-05-29 pull)' },
    { indexId: 'CEU3133320008',   date: '2024-01-01', value: 30.44,   source: 'BLS public API v2 (data.bls.gov, 2026-05-29 pull)' },
    { indexId: 'CEU3133320008',   date: '2024-07-01', value: 30.02,   source: 'BLS public API v2 (data.bls.gov, 2026-05-29 pull)' },
    { indexId: 'CEU3133320008',   date: '2025-01-01', value: 31.56,   source: 'BLS public API v2 (data.bls.gov, 2026-05-29 pull)' },
    { indexId: 'CEU3133320008',   date: '2025-07-01', value: 32.23,   source: 'BLS public API v2 (data.bls.gov, 2026-05-29 pull)' },
    { indexId: 'CEU3133320008',   date: '2026-01-01', value: 34.05,   source: 'BLS public API v2 (data.bls.gov, 2026-05-29 pull)' },
  ],

  // Four clause register entries — same count as the customer-profile seed. Neutral
  // contract language: no section numbers, no "Authority", no "Attachment".
  clauseRegister: [
    { clauseId: 'Standard escalation clause', title: 'Cost escalation',                contractualBasis: 'Customer contract — cost escalation mechanism (standard pricing terms).', appliesTo: 'P001, P002, P003, P006, P007, P008', escalationMechanism: 'Standard escalation clause' },
    { clauseId: 'Indexed basket reconciler',  title: 'Indexed basket reconciler',      contractualBasis: 'Customer contract — supplier basket math against indexed inputs.',         appliesTo: 'P001, P002, P008',                   escalationMechanism: 'Indexed basket reconciler' },
    { clauseId: 'Partial pass-through clause', title: 'Partial pass-through',          contractualBasis: 'Customer contract — escalation with risk-sharing.',                       appliesTo: 'P003, P004',                         escalationMechanism: 'Partial pass-through clause' },
    { clauseId: 'Fixed price',                 title: 'Fixed price (no escalation)',   contractualBasis: 'Customer contract — locked price, supplier carries movement risk.',       appliesTo: 'P005',                               escalationMechanism: 'Fixed price' },
  ],

  strings: {
    pageSubtitle: 'BLS-grounded pricing-index dashboard',
    bidName: 'Sample Bid 2026-Q1',
    escalationClauseLabel: 'Standard escalation clause',
    basketReconcilerLabel: 'Indexed basket reconciler',
    partialClauseLabel: 'Partial pass-through clause',
    escalationMechanismName: 'Standard escalation clause',
    contractName: 'Customer contract',
    attachmentReference: 'Customer pricing schedule v1',
    customerDisplayName: 'Your Organization',
    customerSourceUrl: '',
    customerFactualStatement:
      'Dashboard prepared for the prospect using illustrative seed data.',
    excelTemplatePrefix: 'pricing-index-dashboard-template',
    resetSeedDescription: 'illustrative demo seed',
    resetSeedDatasetDescriptor: 'illustrative demo dataset',
    passthroughFullClauseId: 'Standard escalation clause',
    passthroughPartialClauseId: 'Partial pass-through clause',
    basketReconcilerActualLabel: 'Indexed basket — actual (% since base)',
    partsMasterRecoveryColumnTooltip:
      'Contractual basis for cost recovery (e.g. \'standard escalation clause\'). Used by the Recovery Calculator to attach a basis to each invoice line.',

    // Contract-clause view strings (brand-neutral)
    contractClauseTabSub: 'Indexed basket',
    contractClauseTabBarSub: 'Indexed basket reconciler',
    contractClauseOrientationTooltip:
      'Alternative view: the indexed-basket contract-clause reconciler. Same model, different lens — useful when the conversation is contractual rather than analytical.',
    contractClauseTabTooltip:
      'Alternative view: indexed-basket contract-clause reconciler. Same data, contract-clause lens.',
    contractClauseHeaderSubtitlePrefix: 'Indexed basket reconciler',
    contractClauseHeaderSubtitleSuffix: 'illustrative workspace',
    contractClauseBasePriceTooltip:
      'Contract base price per unit at the base date — the dollar baseline the indexed-basket movement is applied against to derive the justified increase and any pending claim\'s dollar value. Editing it re-scales every downstream $ figure on this dashboard.',
    contractClauseNextReviewTooltip:
      'Next scheduled price-review date under the standard escalation clause. Until this date arrives, any supplier-initiated repricing is voluntary on the customer\'s side. Drives the \'days until review\' signal card and gates the negotiation posture commentary.',
    contractClausePendingClaimTooltip:
      'The %-price-increase the supplier has currently asked for, off-cycle. Used in three places: (1) compared against the indexed-basket movement to flag over- or under-claiming; (2) converted to a dollar figure via Base price; (3) pre-fills the negotiation reconciler input below.',
    contractClauseHeroTitle: 'Indexed basket',
    contractClauseHeroTooltip:
      'Solid line = real BLS data weighted per the indexed-basket schedule. Dashed line = forward projection under the selected scenario.',
    contractClauseBasketSectionTitle: 'Agreed indexed-basket indices',
    contractClauseWeightInputTooltip:
      'Weight of this index in this supplier\'s indexed basket. Indexes the share of the BOM that this commodity drives. All weights for a supplier should sum to 100% — the warning icon flags if they don\'t. Changes recompute the justified basket movement live.',
    contractClauseReconcilerIntro:
      'Supplier is asking for a price increase. What does the indexed basket justify?',
    contractClauseReconcilerTooltip:
      'Plug in the supplier\'s claimed increase. The tool computes what the indexed basket actually justifies, and shows the gap in pp and $ per unit.',
    contractClauseMathIndexTooltip:
      'BLS series code making up the indexed basket. Click header to sort.',
    contractClauseMathWeightTooltip:
      'Weight in the basket per the indexed-basket schedule. The weights sum to 100%. Click header to sort.',
    contractClauseMovementSinceBaseLabel: 'Indexed-basket movement since base',
    contractClausePassThroughTooltip:
      'Customer pays a fraction of the full escalation, depending on the qualifying-share rules and change-order exclusions in the customer contract.',
    contractClauseBidSkusMatchedIndexTooltip:
      'BLS series this SKU maps to under the indexed basket. Click header to sort.',
    contractClauseBidSkusCoverageTooltip:
      'Whether this SKU is covered by an agreed indexed-basket index. Uncovered → no contractual escalation route. Click header to sort.',
    contractClauseEmptyStateNote:
      'Per-supplier indices typically come from the signed pricing schedule. Until that is loaded, this slot stays in TBD state by design — the dashboard never invents supplier data.',
    contractClauseUserGuideText:
      'alternative indexed-basket reconciler lens; same data, contract-clause math.',
    contractClauseFooterDisclaimer:
      'Illustrative workspace — index values are real BLS data; supplier and bid SKU records are sample data for demonstration only.',
    contractClauseCustomerSourceLinkLabel: 'Customer (corporate identity)',
    basketKpiUnitLabel: 'per unit set',
  },

  // ─── Contract-clause view data (brand-neutral precision-machining flavour) ──
  // Precision-machining-appropriate 5-index basket. Weights sum to 1.0.
  // No transportation / electrical-equipment AHE series — those are the
  // railcar-flavoured series used by the original branded profile and have
  // no place in a precision-mfg demo. Replaced with machinery-mfg and
  // fabricated-metal-mfg labour
  // (NAICS 333 / 332) which are the natural cost drivers for a precision
  // machining contract manufacturer (aerospace, medical, semicon, industrial).
  contractClauseBasket: [
    { indexCode: 'WPU10',           weight: 0.30, clauseLabel: 'Raw bar stock — metals PPI (steel, aluminum, copper)' },
    { indexCode: 'WPUID61113',      weight: 0.20, clauseLabel: 'Materials inputs — durable mfg PPI' },
    { indexCode: 'PCU332710332710', weight: 0.20, clauseLabel: 'Machine shops PPI (NAICS 332710)' },
    { indexCode: 'CEU3133200008',   weight: 0.15, clauseLabel: 'Fabricated metal manufacturing wages (AHE, NAICS 332)' },
    { indexCode: 'CEU3133320008',   weight: 0.15, clauseLabel: 'Industrial machinery manufacturing wages (AHE, NAICS 3332)' },
  ],

  contractClauseIdentity: {
    customer: {
      fullName: 'Your Organization',
      sourceUrl: '',
    },
    spendkey: {
      domain: 'spendkey.io',
      sourceUrl: 'https://www.spendkey.io',
    },
  },

  contractClauseSuppliers: [
    {
      id: 'frame-supplier',
      name: 'Precision components supplier',
      segment: 'Machined components',
      baseDate: '2024-11-15',
      basePrice: 184_500,
      unitLabel: 'per unit set',
      nextReviewDate: '2026-06-30',
      contractRef: '[Sample contract reference]',
      reviewCadence: 'Annual',
      passThroughPct: 0.90,
      passThroughSource: 'tbd',
      contractDataSource: 'placeholder',
      indices: [
        { indexCode: 'WPU10',           weight: 0.30, weightSource: 'contract' },
        { indexCode: 'WPUID61113',      weight: 0.20, weightSource: 'contract' },
        { indexCode: 'PCU332710332710', weight: 0.20, weightSource: 'contract' },
        { indexCode: 'CEU3133200008',   weight: 0.15, weightSource: 'contract' },
        { indexCode: 'CEU3133320008',   weight: 0.15, weightSource: 'contract' },
      ],
      pendingClaim: {
        claimedPct: 0.094,
        receivedDate: '2026-05-08',
        rationale: '[Illustrative pending claim — sample data for demonstration]',
        source: 'placeholder',
      },
      pastClaims: [],
    },
    {
      id: 'supplier-slot-b',
      name: '[Supplier slot B — populate from supplier roster]',
      segment: '',
      baseDate: '',
      basePrice: 0,
      unitLabel: '',
      nextReviewDate: '',
      contractRef: '',
      reviewCadence: '',
      passThroughPct: 0,
      passThroughSource: 'tbd',
      contractDataSource: 'tbd',
      indices: [],
      pastClaims: [],
    },
    {
      id: 'supplier-slot-c',
      name: '[Supplier slot C — populate from supplier roster]',
      segment: '',
      baseDate: '',
      basePrice: 0,
      unitLabel: '',
      nextReviewDate: '',
      contractRef: '',
      reviewCadence: '',
      passThroughPct: 0,
      passThroughSource: 'tbd',
      contractDataSource: 'tbd',
      indices: [],
      pastClaims: [],
    },
  ],

  contractClauseBidSkus: [
    {
      partNumber: '[Sample SKU 1]',
      description: 'Inconel 718 turbine shaft, machined',
      dataPoints: 8,
      matchedIndex: 'WPU10',
      unitCostEstimate: 2_950,
      indexMovement12mo: 1.128,
      coverage: 'partial',
      recommendedBufferPct: 0.045,
      rationale: 'Inconel surcharges tracking metals PPI; partial pass-through expected on nickel alloy premiums.',
    },
    {
      partNumber: '[Sample SKU 2]',
      description: 'Ti-6Al-4V medical implant blank, ground',
      dataPoints: 6,
      matchedIndex: 'WPU10',
      unitCostEstimate: 1_820,
      indexMovement12mo: 1.142,
      coverage: 'partial',
      recommendedBufferPct: 0.060,
      rationale: 'Titanium bar stock up double-digits YoY; standard escalation covers most, residual buffer for DFARS-domestic-melt premium.',
    },
    {
      partNumber: '[Sample SKU 3]',
      description: '7075 aluminum aerospace bracket, 5-axis milled',
      dataPoints: 9,
      matchedIndex: 'WPU10',
      unitCostEstimate: 640,
      indexMovement12mo: 1.084,
      coverage: 'covered',
      recommendedBufferPct: 0.0,
      rationale: 'Aluminum within agreed basket movement; no buffer needed.',
    },
    {
      partNumber: '[Sample SKU 4]',
      description: '316LVM surgical cannula, swiss-turned',
      dataPoints: 11,
      matchedIndex: 'WPUID61113',
      unitCostEstimate: 385,
      indexMovement12mo: 1.061,
      coverage: 'covered',
      recommendedBufferPct: 0.0,
      rationale: 'Tracks materials inputs PPI; covered by indexed basket.',
    },
    {
      partNumber: '[Sample SKU 5]',
      description: '17-4PH stainless valve body, wire-EDM',
      dataPoints: 4,
      matchedIndex: 'WPU10',
      unitCostEstimate: 1_440,
      indexMovement12mo: 1.097,
      coverage: 'partial',
      recommendedBufferPct: 0.055,
      rationale: '17-4PH carries process premium; partial buffer for heat-treat lead-time risk.',
    },
    {
      partNumber: '[Sample SKU 6]',
      description: 'PEEK CNC-machined fixture, food-grade',
      dataPoints: 2,
      matchedIndex: '—',
      unitCostEstimate: 920,
      indexMovement12mo: 1.0,
      coverage: 'uncovered',
      recommendedBufferPct: 0.11,
      rationale: 'Specialty polymer outside indexed basket; only 2 historical purchases — Limited trend confidence.',
    },
  ],

  contractClauseAssumptions: [
    {
      topic: 'Index time series (5 BLS series — all in basket)',
      source: 'bls',
      detail: 'Precision-machining basket: WPU10 (metals PPI), WPUID61113 (durable mfg materials PPI), PCU332710332710 (machine shops PPI, NAICS 332710), CEU3133200008 (AHE fabricated metal mfg, NAICS 332) and CEU3133320008 (AHE industrial machinery mfg, NAICS 3332). All five are real published BLS time series — fetched directly from data.bls.gov via the public API.',
    },
    {
      topic: 'Indexed-basket weights',
      source: 'contract',
      detail: 'Weights are sample programme-level weightings. Sum = 1.0. Per-supplier weightings would require confirmation of whether each supplier has its own basket.',
    },
    {
      topic: 'Supplier identities',
      source: 'placeholder',
      detail: 'One supplier slot is populated with a generic label ("Precision components supplier"). The other two slots are marked TBD — placeholder for a real supplier roster.',
    },
    {
      topic: 'Contract terms per supplier (base price, base date, review cadence, pass-through %)',
      source: 'placeholder',
      detail: 'Placeholder values. In production these are read from the signed pricing schedule per supplier.',
    },
    {
      topic: 'Pending claim figure (9.4%)',
      source: 'placeholder',
      detail: 'Illustrative figure used to seed the reconciler. Production would wire this to the customer\'s claim intake channel (email / portal).',
    },
    {
      topic: 'Pass-through scenario brackets (100 / 80 / 60 / 40 %)',
      source: 'derived',
      detail: 'A four-step bracket to expose customer-side recovery sensitivity. The operative pass-through for any given contract comes from the customer-side escalation clause and qualifying-share rules.',
    },
    {
      topic: 'Bid SKU list',
      source: 'placeholder',
      detail: 'Six illustrative SKU rows for layout. Production reads from the sourcing team\'s bid SKU file.',
    },
    {
      topic: '"Today" for elapsed-time calculations',
      source: 'derived',
      detail: 'Pinned at 20 May 2026 to align with the latest BLS pull and keep the demo reproducible. Production uses the actual current date.',
    },
    {
      topic: 'Forecast — workbook scenarios (Best / Base / Recent / Worst)',
      source: 'contract',
      detail: 'Annual % change rates per index are sample scenario constants used for the forecast preview.',
    },
    {
      topic: 'Forecast — Spendkey statistical projection',
      source: 'derived',
      detail: 'Trailing-CAGR via log-linear OLS on the last 36 monthly BLS observations per index. Exposed as an optional second curve for comparison.',
    },
  ],

  contractClauseDecisions: [
    {
      topic: 'Justified % comes from the indexed-basket — never a single index',
      decision: 'Per-index basket weight × (current/base index value), summed across all five basket indices.',
      backing: 'Single source of truth; selecting any single index would be fragile and contract-non-compliant.',
    },
    {
      topic: 'Gap is shown in $ per unit, not only %',
      decision: 'Renegotiation surface always renders the gap as percentage points AND dollars per unit.',
      backing: 'A concrete dollar figure converts a percentage debate into a defensible counter-offer.',
    },
    {
      topic: 'Pre-fill the reconciler with the pending claim',
      decision: 'When a supplier has a claim on file, pre-populate the reconciler input with their figure.',
      backing: 'Most common entry-flow is "supplier emailed asking for X — what do I tell them?". The dashboard answers that without any typing.',
    },
    {
      topic: 'Rising index = amber, not red',
      decision: 'A rising agreed index is rendered amber (warning), not red (danger).',
      backing: 'A rising index is the expected condition under a working contract — it triggers the agreed escalation mechanism.',
    },
    {
      topic: 'Customer pass-through is shown as a four-step bracket',
      decision: 'The Customer pass-through scenarios table surfaces 100% / 80% / 60% / 40% scenarios side by side.',
      backing: 'Customer-side recovery is rarely 100% in practice — the bracket exposes sensitivity without re-running the calculation.',
    },
    {
      topic: 'Bid buffer flags low-confidence rows',
      decision: 'SKUs with fewer than 2 historical data points are visually amber-flagged on the data-points column.',
      backing: 'A single purchase point is no trend. We surface the row but flag the low confidence visually rather than hiding it.',
    },
    {
      topic: 'Insights are narrative, not just KPI tiles',
      decision: '1–3 sentences of human-readable narrative per insight card, not just a number.',
      backing: 'Buyers use these to brief upward and to push back on suppliers. Sentences are paraphrasable; bare numbers require interpretation.',
    },
    {
      topic: 'Every number carries its provenance',
      decision: 'Cards and rows carry small badges: "BLS" for real-data, "contract" for contract-sourced, "placeholder" / "TBD" otherwise.',
      backing: 'Spendkey is selling traceability. A demo that doesn\'t distinguish data from placeholder undermines the value proposition.',
    },
  ],

  userStoriesLedger: buildUserStories({
    copy: { basketReconcilerWhereLabel: 'indexed-basket math' },
    anonymize: true,
  }),
  userStoriesLedgerShowStatusBadges: false,

  showUserStoriesLedger: true,
  showContractClauseView: true,
  showCustomerIdentityFooter: false,
};
